/**
 * Keeps a long recording small enough to hold in a browser tab.
 *
 * Two independent tools, both applied as the log is read:
 *
 *  - `createFrameSharer` loses nothing. A team update repeats its stint table, driver
 *    averages and lap window unchanged until a lap or a stop alters them, so each
 *    frame points at the previous frame's copy when they are equal. This is what lets
 *    every frame of a 13-hour race fit (about 0.8 GB, against about 0.7 GB for the
 *    first 300,000 frames held separately).
 *  - `createFrameThinner` drops frames. It is for devices that report little memory.
 *
 * ---- Thinning ----
 *
 * Trims a recorded log down to what a replay needs, as it is loaded.
 *
 * A team update is a full snapshot of one team, and Apex sends one every time any
 * cell changes, which for a busy race is more than a million of them. The replay
 * holds every entry in memory, so a long race did not fit and was cut off part-way.
 * Most of those frames only differ in the gap, interval, a sector cell or the
 * position, which a viewer cannot tell apart at replay speed, so only the frames
 * that mean something are kept:
 *
 *  - the first frame of each team;
 *  - any frame where the lap counter, the stop count, the pit state, the lap time,
 *    the driver or the team's identity changed (this is where laps, stops and the
 *    sector times are taken from, so none is lost);
 *  - otherwise one frame every `refreshMs`, to keep gaps and positions current;
 *  - the last frame of each team, so the finishing order is exact.
 *
 * Every other kind of entry is kept as it is.
 */

/** How often a team's gap, interval and position are refreshed when nothing else changed. */
export const FRAME_REFRESH_MS = 15_000;
// Measured on the 13-hour Vratsa race (1.14 million entries): 10 s keeps 302,000, which
// is over the loader's 300,000 cap; 15 s keeps 232,000; 30 s keeps 162,000. The laps,
// stops and sector times found are the same at every setting.

export interface ThinnableEntry {
  t: number;
  kind: string;
  data: unknown;
}

interface Frame {
  id?: string;
  lapCount?: number;
  pitCount?: string;
  inBox?: boolean;
  lastLap?: string;
  driver?: string;
  kartNumber?: string;
  teamName?: string;
  category?: string;
}

function signature(f: Frame): string {
  return [
    f.lapCount, f.pitCount, f.inBox ? 1 : 0, f.lastLap,
    f.driver, f.kartNumber, f.teamName, f.category,
  ].join('|');
}

export interface FrameThinner {
  /** True if the entry should be kept. Call in log order. */
  keep: (entry: ThinnableEntry) => boolean;
  /** The latest frame of each team that was dropped; add these once the log is read. */
  flush: () => ThinnableEntry[];
}

export function createFrameThinner(refreshMs: number = FRAME_REFRESH_MS): FrameThinner {
  const last = new Map<string, { t: number; sig: string }>();
  /** Per team, the newest frame that was dropped since the last one kept. */
  const pending = new Map<string, ThinnableEntry>();

  return {
    keep(entry) {
      if (entry.kind !== 'teamUpdate') return true;
      const frame = entry.data as Frame | null;
      const id = frame?.id;
      if (!frame || typeof id !== 'string') return true;

      const sig = signature(frame);
      const prev = last.get(id);
      // A step backwards in time (two recordings joined) is never worth guessing about.
      const unchanged =
        prev !== undefined && prev.sig === sig && entry.t >= prev.t && entry.t - prev.t < refreshMs;

      if (unchanged) {
        pending.set(id, entry);
        return false;
      }
      last.set(id, { t: entry.t, sig });
      pending.delete(id);
      return true;
    },

    flush() {
      const rest = Array.from(pending.values());
      pending.clear();
      return rest;
    },
  };
}

// ── Sharing ──────────────────────────────────────────────────────────────────

const SHARED_FIELDS = ['stintAverages', 'driverSessionAverages', 'lastLapTimes'] as const;

export interface FrameSharer {
  /** Points the entry's repeated fields at the team's previous copy when equal. Call in log order. */
  share: (entry: ThinnableEntry) => void;
}

export function createFrameSharer(): FrameSharer {
  const previous = new Map<string, Partial<Record<(typeof SHARED_FIELDS)[number], { json: string; value: unknown }>>>();

  return {
    share(entry) {
      if (entry.kind !== 'teamUpdate') return;
      const frame = entry.data as Record<string, unknown> | null;
      const id = frame?.id;
      if (!frame || typeof id !== 'string') return;

      let team = previous.get(id);
      if (!team) {
        team = {};
        previous.set(id, team);
      }
      for (const field of SHARED_FIELDS) {
        const value = frame[field];
        if (value === undefined) continue;
        const json = JSON.stringify(value);
        const before = team[field];
        if (before && before.json === json) {
          frame[field] = before.value;
        } else {
          team[field] = { json, value };
        }
      }
    },
  };
}

// ── Choosing ─────────────────────────────────────────────────────────────────

/** Devices that report this much memory or less get the thinned replay. */
export const LIGHT_REPLAY_MAX_GB = 4;

/**
 * Whether to drop frames for this device. `navigator.deviceMemory` is only offered
 * by Chromium and rounds to a power of two (0.25 to 8); where it is missing the
 * device is treated as capable, since that is every desktop Safari and Firefox.
 */
export function shouldThin(deviceMemoryGb: number | undefined): boolean {
  return typeof deviceMemoryGb === 'number' && deviceMemoryGb > 0 && deviceMemoryGb <= LIGHT_REPLAY_MAX_GB;
}
